/* eslint-disable no-unsafe-optional-chaining */
import {
    AdvancedOptions,
    Campaign,
    CampaignStats,
    CampaignTemplate,
    CampaignTemplateStats,
    CampaignType,
    ChannelChartData,
    ChannelChartDataStats,
    TrackedLink,
} from '@dev/ee-api/ee-api-models';
import { formatNumber, palette } from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';
import { cloneDeep, filter, find, forEach, map, omit } from 'lodash-es';
import numeral from 'numeral';
import { parse, stringifyUrl } from 'query-string';
import { AbSplitDisplayMode, TemplateNameInterface } from '../../../../store/campaigns/types';

export const getAbSplitTemplateClicksAndOpens = (
    allTemplatesClicksAndOpens: ChannelChartData[],
    currentTemplate: string,
    dataDisplayMode: AbSplitDisplayMode
): ChannelChartDataStats[] => {
    if (!allTemplatesClicksAndOpens || !currentTemplate || !dataDisplayMode) {
        return null;
    }

    const template: ChannelChartData = find(allTemplatesClicksAndOpens, (template: ChannelChartData) =>
        dataDisplayMode === AbSplitDisplayMode.TestResult ? template.ChannelName === currentTemplate : template.IsWinner
    );
    return template?.Stats;
};

export const getCampaignSimpleStatValue = (
    campaign: Campaign,
    statName: keyof CampaignStats,
    templateStats?: CampaignTemplateStats[],
    currentTemplate?: string,
    dataDisplayMode?: AbSplitDisplayMode
): number => {
    switch (campaign?.Type) {
        case CampaignType.AbSplit:
            if (templateStats && currentTemplate && dataDisplayMode) {
                return +getTemplateSimpleStat(statName, templateStats, currentTemplate, dataDisplayMode);
            }
            return (campaign?.Stats[statName] && +campaign?.Stats?.[statName]) || 0;
        case CampaignType.SendNow:
        case CampaignType.Automated:
            return (campaign?.Stats[statName] && +campaign?.Stats[statName]) || 0;
        default:
            return 0;
    }
};

export const getTemplateSimpleStat = (
    statName: keyof CampaignStats,
    templateStats: CampaignTemplateStats[],
    currentTemplate: string,
    dataDisplayMode: AbSplitDisplayMode
): string => {
    const template: CampaignTemplateStats = find(templateStats, (singleTemplateStats: CampaignTemplateStats) =>
        dataDisplayMode === AbSplitDisplayMode.TestResult
            ? singleTemplateStats.Name === currentTemplate
            : singleTemplateStats.IsWinner
    );

    return template.Stats[statName]?.toString();
};

export const getRecipientsCount = (
    campaign: Campaign,
    templateStats?: CampaignTemplateStats[],
    currentTemplate?: string,
    dataDisplayMode?: AbSplitDisplayMode
): string => {
    switch (campaign?.Type) {
        case CampaignType.AbSplit:
            if (templateStats && currentTemplate && dataDisplayMode) {
                return getTemplateSimpleStat('Recipients', templateStats, currentTemplate, dataDisplayMode);
            }
            return campaign.RecipientsCount?.toString() || '0';
        case CampaignType.SendNow:
            return campaign.RecipientsCount?.toString() || '0';
        case CampaignType.Automated:
            return campaign?.Stats?.Delivered?.toString() || '0';
        default:
            return '0';
    }
};

export const getCampaignStartDate = (campaign: Campaign, format?: string): string => {
    const startDate =
        campaign?.Type === CampaignType.Automated ? campaign?.DateAdded : campaign?.TriggerOptions?.DateToSendOn;
    return startDate ? convertToLocalTime(startDate, format) : null;
};

export const isLinkClickTrackingEnabled = (advancedOptions: AdvancedOptions): boolean =>
    advancedOptions.EnableLinkClickTracking;

export const isClickTrackingCampaignEnabled = (campaign: Campaign): boolean => campaign?.TrackingOptions?.TrackClicks;

export const isOpenTrackingCampaignEnabled = (campaign: Campaign): boolean => campaign?.TrackingOptions?.TrackOpens;

export const isClickTrackingEnabled = (campaign: Campaign, advancedOptions: AdvancedOptions): boolean =>
    campaign?.TrackingOptions?.TrackClicks && advancedOptions?.EnableClickTracking;

export const isOpenTrackingEnabled = (campaign: Campaign, advancedOptions: AdvancedOptions): boolean =>
    campaign?.TrackingOptions?.TrackOpens && advancedOptions?.EnableOpenTracking;

export const extractTemplateName = (template: CampaignTemplate): TemplateNameInterface => ({
    displayName: template?.Body?.Subject || template.TemplateName,
    backendName: template.TemplateName,
    channelName: template.Name,
});

export const getCampaignTemplateNames = (campaign: Campaign, isWithTestResult: boolean): TemplateNameInterface[] => {
    if (isWithTestResult) {
        return map(campaign?.CampaignTemplates, extractTemplateName);
    }
    // old campaigns don't have separated winner template, so template with IsWinner and IsWinnerTemplate set to true should be returned
    return campaign?.CampaignTemplates?.filter(
        (template) => !template.IsWinner || (template.IsWinner && template.IsWinnerTemplate)
    )?.map(extractTemplateName);
};

const templateClickCountCss = `
@import url(https://fonts.googleapis.com/css?family=Nunito);
a {display: inline-block; position: relative;}
.clickCountTooltip {
    box-sizing: border-box;
    min-width: 57px;
    display: inline-flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    background: ${palette.teal[2]};
    border: 1px solid ${palette.teal[7]};
    color: ${palette.gray[7]};
    font-size: 12px;
    font-family: 'Nunito';
    border-radius: 4px;
    padding: 4px 8px;
    transition: width .3s ease-out, height .3s ease-out, padding .3s ease-out, transform .3s;
    transform: translateY(0);
    z-index: 9;
    font-weight:400;
}
.clickCountTooltip:hover {
    padding: 9px 8px 13px;
    z-index: 999999;
}
.clickCountTooltip__countValues {
    display: inline-flex;
    justify-content: space-between;
    margin: 0 0 0px;
    transition: margin .3s ease-out;
    font-family: 'Nunito';
}
.clickCountTooltip:hover .clickCountTooltip__countValues {
    margin: 0 0 2px;
}
.clickCountTooltip__percentage {
    white-space: nowrap;
    font-family: 'Nunito';
}
.clickCountTooltip__nominal {
    display: inline-block;
    white-space: nowrap;
    width: 0;
    margin: 0 0 0 0;
    opacity: 0;
    transition: width .3s ease-out, opacity .3s ease-out, margin .3s ease-out;
    font-family: 'Nunito';
}
.clickCountTooltip:hover .clickCountTooltip__nominal {
    width: auto;
    display: inline-block;
    opacity: 1;
    margin: 0 0 0 65px;
}
.clickCountTooltip__text {
    display: none;
    justify-content: flex-end;
    font-size: 10px;
    margin: 0 0 6.5px 0;
}
.clickCountTooltip:hover .clickCountTooltip__text {
    display: inline-flex;
}
.clickCountTooltip__track {
    display: inline-flex;
    width: 100%;
    height: 0px;
    border-radius: 12px;
    background-color: #fff;
    transition: height .3s ease-out;
}
.clickCountTooltip:hover .clickCountTooltip__track {
    height: 4px;
}
.clickCountTooltip__progress {
    display: inline-block;
    height: 100%;
    border-radius: 12px;
    background-color: ${palette.indigo[6]};
}

`;

const prepareLinkClickTooltip = (link: TrackedLink): HTMLElement => {
    const percentString = `${link.Percent}%`;
    const tooltip = document.createElement('span');
    tooltip.classList.add('clickCountTooltip');
    tooltip.innerHTML = `
        <span class="clickCountTooltip__countValues">
            <span class="clickCountTooltip__percentage">${percentString}</span><span class="clickCountTooltip__nominal">${formatNumber(
        +link.Clicks
    )}</span>
        </span>
        <span class="clickCountTooltip__text">Total clicks</span>
        <span class="clickCountTooltip__track">
            <span style="width: ${percentString}" class="clickCountTooltip__progress">
            </span>
        </span>
    `;
    return tooltip;
};

type GroupValuesForLinksWIthMergeFiledsT = {
    links: TrackedLink[];
    href: string;
    currentPosition: number;
};

const groupValuesForLinksWithMergeFields = ({ links, href, currentPosition }: GroupValuesForLinksWIthMergeFiledsT) =>
    // For each anchor href find corresponding link clicks
    filter(links, (link) => {
        // Escape all RegExp special characters except "{" and "}" (mergetags) in the href
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
        const escapedHref = href.replace(/[.*+?^$()|[\]\\]/g, '\\$&');
        // Dynamically create a RegExp rule for anchor's href
        const regExpRule = escapedHref.replace(/\{.*\}/g, '.*');
        const regExp = new RegExp(regExpRule, 'g');
        return regExp.test(link.Link) && link.Position === currentPosition;
    }).reduce(
        (sum, link: TrackedLink) => ({
            Link: link.Link, //href previously
            Clicks: (+link.Clicks + +sum.Clicks).toString(),
            UniqueClicks: (+link.UniqueClicks + +sum.UniqueClicks).toString(),
            Percent: numeral(
                +link.Percent.replace(' ', '').replace('%', '') + +sum.Percent.replace(' ', '').replace('%', '')
            ).format('0.00'),
        }),
        {
            Link: '',
            Clicks: '0',
            UniqueClicks: '0',
            Percent: '0',
        }
    );

const tryDecodeLink = (href: string) => {
    try {
        return decodeURIComponent(href);
    } catch {
        return href;
    }
};

export const injectTemplateClickCountsHtml = (html: string, linksList: TrackedLink[], isMobile: boolean): string => {
    const templateDocument = document.createElement('html');
    let links = cloneDeep(linksList);

    const currentPositionsOfLinks = new Map();

    templateDocument.innerHTML = html;
    const head = templateDocument.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);
    style.setAttribute('type', 'text/css');
    style.appendChild(document.createTextNode(templateClickCountCss));

    // Get all <a href></a> with a href attr that's non empty and doesn't start with mergetag("{")
    // (For now - We have no option to display counts of links created from just mergetags)
    const templateAnchorNodes = templateDocument.querySelectorAll('a[href]:not([href=""]):not([href^="{"])');

    // Jump through all anchors in the template HTML
    forEach(templateAnchorNodes, (anchor: HTMLObjectElement) => {
        const href = tryDecodeLink(anchor.getAttribute('href'));

        // Adding relative url, so it will go
        anchor.classList.add('linkWithTooltip');
        // Check if anchor's href has a mergetag
        const hasMergeTag = href.indexOf('{') > 0 && href.indexOf('{') < href.indexOf('}');

        let linkData;
        const currentPosition = currentPositionsOfLinks?.get(href) || 0;
        if (hasMergeTag) {
            linkData = groupValuesForLinksWithMergeFields({ links, href, currentPosition });

            currentPositionsOfLinks.set(href, currentPosition + 1);
        } else {
            let linkWithoutSearch: string;
            linkData = find(links, (link) => {
                const [urlNoQuery = '', linkParams = ''] = link?.Link?.split?.('?');
                linkWithoutSearch = urlNoQuery;
                // linkWithoutSearch = link?.Link?.split?.('?')?.[0];
                const linkWithoutBackendEncoding = href.replace(/\\/g, '');

                //we are omiting utms params from backend links, as thery are not in template hrefs.
                const paramsWithOmitedUTMs = omit(parse(linkParams, { sort: false }), [
                    'utm_campaign',
                    'utm_source',
                    'utm_medium',
                ]);

                const linkWithoutUtm = stringifyUrl(
                    { url: linkWithoutSearch, query: paramsWithOmitedUTMs },
                    { sort: false }
                );
                const linksToCompare = [
                    link.Link,
                    link?.Link?.replace(/\\/g, ''), // to eliminate some backend safe encoding as some chars sould not go to hrefs,
                    linkWithoutSearch,
                    linkWithoutUtm,
                ];

                return linksToCompare?.includes(linkWithoutBackendEncoding);
            });
            if (linkData) {
                linkData.Percent = linkData?.Percent?.replace(' ', '')?.replace('%', '');
                currentPositionsOfLinks.set(linkWithoutSearch, currentPosition + 1);
            }
        }
        if (linkData) {
            anchor.appendChild(prepareLinkClickTooltip(linkData));
            // Removing link that has been already assign, so the same position would not be reassign once again. Assumption is taht backend is sending Position sorted from 0 to n
            links = links.filter((o) => {
                const linkFromDB = o?.Link;
                const anchorLink = linkData?.Link || href;
                const positionOfLink = linkData?.Position ?? currentPosition;
                return linkFromDB !== anchorLink || (o?.Position !== positionOfLink && linkFromDB === anchorLink);
            });
        } else {
            anchor.appendChild(
                prepareLinkClickTooltip({
                    Link: href,
                    Clicks: '0',
                    UniqueClicks: '0',
                    Percent: '0',
                })
            );
        }
        if (isMobile) {
            anchor.removeAttribute('href');
        }
    });

    return templateDocument.innerHTML;
};
